import PropTypes from 'prop-types';
import axios from 'axios';
import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { normalize } from 'normalizr';
import cx from 'classnames';

import { getLoggedInUser, userHasLimitedProviderRole } from '../selectors/userSelectors';
import HeaderContainer from '../containers/HeaderContainer';
import SessionTimeoutModal from './SessionTimeoutModal';
import SystemAlertMessage from './SystemAlertMessage';
import NavigationWrapper from './NavigationWrapper';
import OverlayContainer from '../containers/OverlayContainer';
import calculateViewportHeight from '../helpers/ViewportHelpers';
import StorageService from '../services/StorageService';
import WebSocketService from '../services/WebSocketService';
import routes from '../routes';
import { systemAlertSelectors } from '../selectors';
import { userHasAnyOfPermissions, userHasAllOfPermissions } from '../helpers/UserHelpers';
import { isMobile, isMobileSafari, isTouchDevice } from '../helpers/BrowserHelpers';
import * as AuthReducer from '../reducers/authReducer';
import store from '../store';
import { BILLING_VIEW, CONTACT_VIEW, CONTACT_EDIT, RHINOPAY_MANAGER_VIEW, CONVERSATION_CONTACT_MOBILE } from '../constants/UserPermissionsConstants';
import * as PayReducer from '../reducers/payReducer';
import * as BillingReducer from '../reducers/billingReducer';
import * as VideoReducer from '../reducers/rhinovideoReducer';
import * as UserReducer from '../reducers/userReducer';
import { user as userSchema } from '../actions/NormalizrSchema';
import PostMessageService from '../services/PostMessageService';
import OutboundPostMessageService from '../services/OutboundPostMessageService';
import AppUpdateService from '../services/AppUpdateService';
import * as FormReducer from '../reducers/formReducer';
import * as RhinocallReducer from '../reducers/rhinocallReducer';
import * as UIReducer from '../reducers/uiReducer';
import RhinocallModal from './RhinocallModal';
import PageLoader from './PageLoader';
import { getHasActiveMobileCall } from '../selectors/rhinocallSelector';
import { isAxiumLoginPage } from '../helpers/AxiumHelpers';
import { getCurrentOrg } from '../selectors/organizationSelectors';
import DockedCallBar from './DockedCallBar';
import VoipMicPermissionBanner from './VoipMicPermissionBanner';
import AuthService from '../services/AuthService';

class App extends Component {
  state = {
    isInitialDataFetched: false,
  }

  _lastSessionExtend = 0;

  _activityHandler = () => {
    WebSocketService.resetPresenceTimeout();
    this.extendSession();
  };

  extendSession() {
    const THROTTLE_MS = 5 * 60 * 1000;
    const now = Date.now();
    if (now - this._lastSessionExtend < THROTTLE_MS) return;
    const userId = StorageService.readEntry('user');
    if (!userId) return;
    this._lastSessionExtend = now;
    axios.get(`/users/${userId}`).catch(() => { this._lastSessionExtend = 0; });
  }

  async componentDidMount() {
    AuthService.setHistory(this.props.history);

    // Consume tabClosedTime immediately so it can never trigger a logout on a later load;
    // checkTabClosedTimeout evaluates it once the org's session timeout is available.
    this._tabClosedTime = localStorage.getItem('tabClosedTime');
    localStorage.removeItem('tabClosedTime');

    try {
      await AuthService.init();
    } catch (err) {
      // Without this the app would sit on the PageLoader forever. Let routes render so
      // AuthLogin can show an error / retry instead.
      console.error('Auth0 init failed:', err); // eslint-disable-line no-console
      this.setState({ isInitialDataFetched: true });
    }

    // --- Unlock Web Audio for browser autoplay policies ---
    const unlockAudio = () => {
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        const ctx = new Ctx();
        const buffer = ctx.createBuffer(1, 1, 22050);
        const src = ctx.createBufferSource();
        src.buffer = buffer;
        src.connect(ctx.destination);
        src.start(0);
      } catch (err) {
        console.warn('Audio unlock failed:', err); // eslint-disable-line no-console
      }
      window.removeEventListener('click', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
    };

    window.addEventListener('click', unlockAudio, { once: true });
    window.addEventListener('touchstart', unlockAudio, { once: true });

    // Set history length manually to check if we can go back in history
    this.props.history.listen(() => {
      let { historyUrls } = this.props.history;
      if (historyUrls === undefined) {
        historyUrls = [];
      }
      if (this.props.history.action === 'PUSH') {
        historyUrls.push(this.props.history.location.pathname);
      }
      if (this.props.history.action === 'POP') {
        historyUrls.pop();
      }
      if (historyUrls.length > 5) {
        historyUrls = historyUrls.slice(1);
      }
      this.props.history.historyUrls = historyUrls;
      this.props.history.canGoBack = historyUrls.length > 1;
    });

    this.intercept();
    this.refreshPageIfAppUpdateAvailable();

    // On /callback the code exchange in AuthService.init() owns navigation. A bare /callback
    // (no code/state/error) has nothing to exchange, so treat it like any other page or the
    // loader never clears.
    const callbackParams = new URLSearchParams(window.location.search);
    const isActiveCallback = this.props.location.pathname === '/callback'
      && (callbackParams.has('code') || callbackParams.has('state') || callbackParams.has('error'));
    if (!isActiveCallback) {
      this.getInitialView();
    }

    calculateViewportHeight();
    this.addWindowEventListeners();
    this.addAccesibeListener();
  }

  shouldComponentUpdate(nextProps) {
    return !((this.props.location.pathname === '/auth' || this.props.location.pathname === '/login') && nextProps.location.pathname === '/signup');
  }

  componentDidUpdate(prevProps) {
    calculateViewportHeight();
    this.checkTabClosedTimeout(prevProps);
  }

  // The desktop beforeunload handler stores `tabClosedTime`. If the tab was closed for longer
  // than the org's session timeout, log out once the org (and so sessionTimeoutMinutes) is known.
  // This used to live in render(), where it dispatched on every render and never consumed the key.
  checkTabClosedTimeout(prevProps) {
    if (!this._tabClosedTime) return;
    const prevTimeout = prevProps.currentOrganization?.sessionTimeoutMinutes;
    const sessionTimeoutMinutes = this.props.currentOrganization?.sessionTimeoutMinutes;
    if (prevTimeout !== undefined || sessionTimeoutMinutes === undefined) return;

    const minutesClosed = Math.floor((Date.now() - new Date(this._tabClosedTime)) / (1000 * 60));
    this._tabClosedTime = null;

    if (minutesClosed >= sessionTimeoutMinutes) {
      store.dispatch(this.props.logout('', StorageService.readEntry('user')))
        .then((response) => {
          if (response === 'legacy') {
            this.props.history.push('/login?legacy=true');
          }
        });
    }
  }

  componentWillUnmount() {
    if (this._activityEvents) {
      this._activityEvents.forEach((event) => window.removeEventListener(event, this._activityHandler));
    }
  }

  addWindowEventListeners() {
    window.addEventListener('storage', (e) => {
      if (e.key === 'org' && e.newValue === null) {
        store.dispatch(this.props.unsetUser());
      }
      if ((e.key === 'authType' || e.key === 'user') && e.newValue === null && e.oldValue !== null) {
        this.handleCrossTabLogout(e.oldValue);
      }
    });

    const events = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];

    events.forEach((event) => window.addEventListener(event, this._activityHandler));
    this._activityEvents = events;
    const teardownEvent = isMobileSafari() ? 'pagehide' : 'beforeunload';
    window.addEventListener(teardownEvent, () => {
      if (isMobile() || isTouchDevice()) {
        WebSocketService.setOfflinePresence();
      } else {
        WebSocketService.teardown();
        const currentTime = new Date().toISOString(); // Get the current time in ISO format
        localStorage.setItem('tabClosedTime', currentTime); // Store the time in localStorage
      }
    });
  }

  // Another tab logged out (its logout thunk wiped localStorage). Do not call auth0.logout again:
  // that tab already ended the Auth0 session. Just tear down and go to the login route so this
  // tab does not sit on a Restricted page with a null currentUser, or 401 its way into a second
  // DELETE /logout and auth0.logout.
  handleCrossTabLogout(previousAuthType) {
    if (this._crossTabLogoutHandled) return;
    const { pathname } = this.props.location;
    const publicPaths = ['/auth', '/login', '/callback', '/signup', '/video', '/memberVideo', '/axium/login', '/clinix/login'];
    if (publicPaths.some((path) => pathname.includes(path))) return;
    this._crossTabLogoutHandled = true;

    WebSocketService.teardown();
    store.dispatch(this.props.unsetUser());
    // store.js JSON-encodes values, so a legacy authType arrives as the string '"legacy"'.
    const wasLegacy = previousAuthType === '"legacy"';
    this.props.history.push(wasLegacy ? '/login?legacy=true' : '/auth');
  }

  addAccesibeListener() {
    const observer = new MutationObserver(() => {
      calculateViewportHeight(true);
    });
    const appContainer = document.getElementById('app');
    observer.observe(appContainer, { attributes: true, attributeFilter: ['style'] });
  }

  // A child page is rendered inside the app navigation while a parent page is not
  isChildPage() {
    const currentPath = this.props.location.pathname;
    const parentPagePaths = [
      '/axium/login',
      '/clinix/login',
      '/login',
      '/auth',
      '/forgot-password',
      '/notfound',
      '/signup',
      '/change-password',
      '/diagnostics',
      '/check-number',
      '/rhinopay',
      '/callback',
      '/video',
      '/memberVideo',
    ];

    return !parentPagePaths.some((path) => currentPath.includes(path));
  }

  // Refresh the page if an app update is available
  refreshPageIfAppUpdateAvailable() {
    this.props.history.block(() => {
      // shouldReloadForUpdate dedupes: one forced reload per detected build, so a stale CDN copy
      // of index.html cannot make every navigation reload the page.
      if (AppUpdateService.shouldReloadForUpdate()) {
        window.location.reload();
        return false;
      }
      return true;
    });
  }

  getInitialView() {
    const userId = StorageService.readEntry('user');
    const orgId = StorageService.readEntry('org');
    const isPatientExperience = StorageService.readEntry('patientExperience');
    const authType = StorageService.readEntry('authType');
    const accessToken = localStorage.getItem('access_token');
    const axiumLogin = sessionStorage.getItem('axiumLogin');

    if (userId && !isAxiumLoginPage(this.props.location)) {
      if ((!['legacy', 'axium'].includes(authType) && !accessToken) || (authType === 'axium' && !axiumLogin)) {
        store.dispatch(this.props.logout())
          .then((response) => {
            if (response === 'legacy') {
              this.props.history.push('/login?legacy=true');
            }
            this.setState({ isInitialDataFetched: true });
          });
      } else {
        this.props.getUser(userId, orgId, isPatientExperience)
          .then((response) => {
            if (response && response.data) {
              const normalized = normalize(response.data, userSchema);
              const { isCcr } = response.data;
              store.dispatch(this.props.setUser(this.props.getUserPayload(normalized)));
              store.dispatch(this.props.setOrg(orgId));
              this.fetchServices({ isCcr, orgId, userId });
              store.dispatch(this.props.fetchInitialData(this.props.location.pathname))
                .then(() => {
                  if (!isCcr) {
                    WebSocketService.setup();
                  }
                  const nextPath = store.dispatch(this.props.redirectUser());
                  this.props.history.push(nextPath);
                })
                .then(() => {
                  this.setState({ isInitialDataFetched: true });
                });
            }
          })
          .catch((err) => {
            console.error('Error fetching user:', err.response || err);
            store.dispatch(this.props.logout())
              .then((response) => {
                if (response === 'legacy') {
                  this.props.history.push('/login?legacy=true');
                }
              });
          });
      }
    } else if (authType === 'auth0' && accessToken && !userId) {
      this.setState({ isInitialDataFetched: true });
    } else {
      this.setState({ isInitialDataFetched: true });
    }

    PostMessageService.initEventListener(this.props.history, this.props.location);
  }

  fetchServices({ orgId, isCcr, userId }) {
    // Rhinoform
    store.dispatch(FormReducer.fetchOrgConfiguration(orgId));
    // Billing
    if (userHasAnyOfPermissions([BILLING_VIEW])) store.dispatch(this.props.fetchBilling());
    const mobilePermissions = (userHasAllOfPermissions([CONTACT_VIEW, CONVERSATION_CONTACT_MOBILE]) ||
      userHasAllOfPermissions([CONTACT_EDIT, CONVERSATION_CONTACT_MOBILE]) ||
      userHasAnyOfPermissions([RHINOPAY_MANAGER_VIEW]));
    // Rhinopay
    if (isCcr) {
      store.dispatch(this.props.fetchMerchantCcr(orgId));
    } else if (isMobile() ? mobilePermissions : userHasAnyOfPermissions([CONTACT_VIEW, CONTACT_EDIT, RHINOPAY_MANAGER_VIEW])) {
      store.dispatch(this.props.fetchMerchant(orgId));
    }
    // Rhinovideo
    if (!window.location.pathname.startsWith('/video') && !window.location.pathname.startsWith('/memberVideo')) {
      store.dispatch(this.props.fetchRhinoVideoConfiguration(orgId, isCcr));
    }
    // Rhinocall
    store.dispatch(this.props.fetchRhinoCallOrganizationConfiguration(orgId, userId));
    store.dispatch(UIReducer.hideSessionTimeoutModal());
  }

  intercept() {
    // Track the token last pushed to native so we only re-send when it rolls
    // over (not on every request).
    let lastNativeAccessToken;
    // The logout thunk wipes authType before auth0.logout has navigated away, so 401s that
    // land in that window would otherwise dispatch a second logout (and a second auth0.logout).
    let logoutDispatched = false;
    const dispatchLogoutOnce = async () => {
      if (logoutDispatched) return;
      logoutDispatched = true;
      await store.dispatch(this.props.logout(null, null, false));
    };
    // REQUEST INTERCEPTOR - Let Auth0 SDK handle everything
    axios.interceptors.request.use(async (config) => {
      const authType = StorageService.readEntry('authType');

      // Only handle Auth0 authentication
      if (authType === 'auth0') {
        try {
          // Auth0 SDK automatically checks expiry and refreshes if needed
          // No need to manually parse JWT or check expiration
          const token = await AuthService.getAccessToken();

          if (token) {
            // AuthService.updateAccessToken already persisted the token and set the axios default
            // header when it rolled over; only the per-request header is needed here.
            // Keep native's stored token in sync when it rolls over, so native
            // downloads (which fetch with the AsyncStorage token via getHeaders)
            // never use a stale/expired token ("jwt expired"). The web keeps its
            // own token fresh here on every request, but native was previously
            // only updated once at login.
            if (token !== lastNativeAccessToken) {
              lastNativeAccessToken = token;
              OutboundPostMessageService.postMessage({
                type: 'createCookie',
                data: { cookie: document.cookie, accessToken: token },
              });
            }
            return {
              ...config,
              headers: {
                ...config.headers,
                Authorization: `Bearer ${token}`,
              },
            };
          }
        } catch (err) {
          console.error('Failed to get access token:', err);
          // Let response interceptor handle auth failure
        }
      }

      return config;
    }, (error) => Promise.reject(error));

    // RESPONSE INTERCEPTOR - Handle 401 with single retry
    axios.interceptors.response.use(undefined, async (err) => {
      const originalRequest = err.config;
      const authType = StorageService.readEntry('authType');

      // Handle 401 for Auth0 users with single retry
      if (err.response?.status === 401 && !originalRequest._retry && authType === 'auth0') {
        originalRequest._retry = true;

        try {
          // The cached token is the one the API just rejected, so bypass the SDK cache. If the
          // retry 401s as well, _retry is already set and it falls through to the logout below.
          const newToken = await AuthService.forceRefresh();

          if (newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;

            return axios(originalRequest);
          }

          if (AuthService.isRedirectingForAuth) {
            return Promise.reject(err);
          }
        } catch (refreshError) {
          console.error('Token refresh failed, logging out');
          if (!AuthService.isRedirectingForAuth && !window.location.href.includes('/login') && !window.location.href.includes('/auth')) {
            await dispatchLogoutOnce();
          }
          return Promise.reject(refreshError);
        }
      }

      // Handle 401 for non-Auth0 users or after retry failed
      if (err.response?.status === 401 &&
        !window.location.href.includes('/login') &&
        !window.location.href.includes('/auth')) {
        await dispatchLogoutOnce();
      }

      return Promise.reject(err);
    });
  }

  render() {
    const appContainerClasses = cx('app-reactroot', {
      'is-dragging': this.props.isDragging,
    });
    let markup;
    const { location, loggedInUser, systemAlert, history, isRhinocallModalVisible, hasActiveMobileCall, isLimitedProvider } = this.props;
    const userIsLoggedInOrCcr = (loggedInUser || StorageService.readEntry('multiOrgs'));
    const appWrapperClass = isLimitedProvider ? 'app-wrapper-limited' : 'app-wrapper';
    if (!this.state.isInitialDataFetched) {
      markup = <PageLoader />;
    } else if (this.isChildPage() && userIsLoggedInOrCcr) {
      markup = (
        <>
          <div className={appContainerClasses} data-vh="100">
            {(isRhinocallModalVisible || hasActiveMobileCall) && <RhinocallModal />}
            {systemAlert && <SystemAlertMessage location={location} history={history} />}
            <div className="app-wrapper__container">
              <NavigationWrapper location={location} history={history} />
              <OverlayContainer location={location} history={history} />
              <div className={appWrapperClass}>
                <SessionTimeoutModal location={location} history={history} />
                <VoipMicPermissionBanner />
                <HeaderContainer location={location} history={history} />
                {routes}
                {/* Always-mounted popup for VoIP calls */}
                {/* <IncomingCallPopup /> */}
                <DockedCallBar />
              </div>
            </div>
          </div>
        </>
      );
    } else {
      markup = (
        <>
          {routes}
        </>
      );
    }
    return markup;
  }
}

App.propTypes = {
  location: PropTypes.object,
  loggedInUser: PropTypes.object,
  systemAlert: PropTypes.object,
  history: PropTypes.object,
  logout: PropTypes.func,
  setUser: PropTypes.func,
  setOrg: PropTypes.func,
  fetchInitialData: PropTypes.func,
  redirectUser: PropTypes.func,
  getUser: PropTypes.func,
  fetchMerchantCcr: PropTypes.func,
  fetchMerchant: PropTypes.func,
  fetchBilling: PropTypes.func,
  fetchRhinoVideoConfiguration: PropTypes.func,
  getUserPayload: PropTypes.func,
  fetchRhinoCallOrganizationConfiguration: PropTypes.func,
  isRhinocallModalVisible: PropTypes.bool,
  isDragging: PropTypes.bool,
  hasActiveMobileCall: PropTypes.bool,
  isLimitedProvider: PropTypes.bool,
  unsetUser: PropTypes.func,
  currentOrganization: PropTypes.object,
};

const mapStateToProps = (state) => ({
  loggedInUser: getLoggedInUser(state),
  ui: state.ui,
  systemAlert: systemAlertSelectors.getActiveSystemAlert(state),
  updateUser: UserReducer.updateUserEmail,
  getUser: UserReducer.getUser,
  getUserPayload: UserReducer.getUserPayload,
  setUser: AuthReducer.setUser,
  setOrg: AuthReducer.setOrg,
  fetchInitialData: AuthReducer.fetchInitialData,
  redirectUser: AuthReducer.redirectUser,
  logout: AuthReducer.logout,
  fetchMerchant: PayReducer.fetchMerchant,
  fetchMerchantCcr: PayReducer.fetchMerchantCcr,
  fetchBilling: BillingReducer.fetchBilling,
  fetchRhinoVideoConfiguration: VideoReducer.fetchRhinoVideoConfiguration,
  fetchRhinoCallOrganizationConfiguration: RhinocallReducer.fetchRhinoCallOrganizationConfiguration,
  isRhinocallModalVisible: state.rhinocall.isRhinocallModalVisible,
  isDragging: state.rhinocall.isDragging,
  hasActiveMobileCall: getHasActiveMobileCall(state),
  isLimitedProvider: userHasLimitedProviderRole(state),
  currentOrganization: getCurrentOrg(state),
});

const actions = {
  unsetUser: AuthReducer.unsetUser,
};

export default withRouter(connect(mapStateToProps, actions)(App));
